import React, { useEffect, useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { queryKeys } from '../../lib/queryKeys';
import { useToast } from '../../contexts/ToastContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
    allowedTargets, lifecycleStatusValue, ownerActionFor, toLifecycleStatus,
    type LifecycleStatus, type OwnerActionView,
} from '../../lib/bookingLifecycle';
import s from './BookingDetail.module.css';

/**
 * Who is acting. Owners change status through the owner state-machine endpoint;
 * staff (S1, AB#2204) through the self-scoped salon-staff endpoint, which the
 * backend validates with the SAME lifecycle rules and side effects (own bookings
 * only). The actions shown are the backend's `allowedTransitions` either way.
 */
export type BookingActor = 'owner' | 'staff';

/** Sends one status change for the given actor. */
export function runBookingTransition(actor: BookingActor, appointmentId: number, target: LifecycleStatus): Promise<boolean> {
    const value = lifecycleStatusValue(target);
    return actor === 'staff'
        ? apiService.updateAppointmentStatus(appointmentId, value)
        : apiService.changeOwnerAppointmentStatus(appointmentId, value);
}

export interface BookingActionsProps {
    appointmentId: number;
    /** Current status (wire name or legacy int). */
    status: number | string;
    /** Backend-allowed targets for this booking (see lib/bookingLifecycle). */
    allowedTransitions?: string[] | null;
    /**
     * Optional subset filter — the Appointments list renders only Confirm /
     * Decline inline on a pending row; the full set lives in the booking detail.
     */
    only?: LifecycleStatus[];
    /** Hide the "Update status" heading (inline row use). */
    compact?: boolean;
    /** Called after a successful change (e.g. close the dialog). */
    onChanged?: (target: LifecycleStatus) => void;
    /** S1 (AB#2204) — `staff` sends the change through the salon-staff endpoint. Default `owner`. */
    actor?: BookingActor;
}

/**
 * O1 (AB#2199, SO-04 / SO-06) — the ONE owner status-action control, used by the
 * Calendar booking detail, the Appointments booking detail and the pending-row
 * inline Confirm / Decline — for owners AND (S1, AB#2204) for staff on their own
 * bookings. It renders exactly the backend's allowedTransitions (never its own
 * copy of the lifecycle), sends every change through the owner state-machine
 * endpoint (AB#2109) or, for staff, the self-scoped staff status endpoint, and asks for confirmation before a Decline or
 * Cancel because the customer is notified (AB#2164). Afterwards it refreshes the
 * Calendar, Appointments and Dashboard caches so every surface shows the new state.
 */
const BookingActions: React.FC<BookingActionsProps> = ({
    appointmentId, status, allowedTransitions, only, compact = false, onChanged, actor = 'owner',
}) => {
    const { t } = useLanguage();
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const [busy, setBusy] = useState(false);
    // After a successful change the old actions are gone for good: they never
    // flicker back between the response and the refreshed row (review minor 1).
    const [done, setDone] = useState(false);
    const [pendingConfirm, setPendingConfirm] = useState<OwnerActionView | null>(null);
    const keepRef = useRef<HTMLButtonElement>(null);
    const promptId = useId();

    const from = toLifecycleStatus(status);
    const actions = allowedTargets(allowedTransitions)
        .filter(target => !only || only.includes(target))
        .map(target => ownerActionFor(from, target));

    // a11y — when the confirm step opens, focus the safe choice.
    useEffect(() => {
        if (pendingConfirm) keepRef.current?.focus();
    }, [pendingConfirm]);

    // Calendar + Appointments caches (the Dashboard's today view reads the same
    // appointments / calendar keys; its period summary reloads via onChanged).
    const refresh = () => Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all }),
    ]);

    const apply = async (view: OwnerActionView) => {
        if (busy) return;
        setBusy(true);
        try {
            await runBookingTransition(actor, appointmentId, view.target);
            showToast(t(view.toastKey), 'success');
            setPendingConfirm(null);
            setDone(true);
            void refresh();
            onChanged?.(view.target);
        } catch {
            showToast(t('calendar.peek.statusUpdateFailed'), 'error');
            // The backend said no — never keep offering the same confirm step.
            setPendingConfirm(null);
            // A rejection usually means the booking moved on elsewhere (e.g. the
            // customer cancelled) — refresh so every view drops the stale state.
            void refresh();
        } finally {
            setBusy(false);
        }
    };

    const onClick = (view: OwnerActionView) => {
        if (busy) return;
        if (view.needsConfirm) setPendingConfirm(view);
        else void apply(view);
    };

    if (done) return null;

    if (pendingConfirm) {
        return (
            <div
                className={compact ? s.actionsCompact : s.actions}
                role="alertdialog"
                aria-label={t(pendingConfirm.labelKey)}
                aria-describedby={promptId}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => {
                    // Escape backs out of the confirm step only (not the whole dialog).
                    if (e.key === 'Escape') {
                        e.stopPropagation();
                        // Keep focus inside the dialog so the next Escape closes it.
                        (e.currentTarget.closest('[role="dialog"]') as HTMLElement | null)?.focus();
                        setPendingConfirm(null);
                    }
                }}
            >
                <p id={promptId} className={s.confirmPrompt}>{t(pendingConfirm.promptKey ?? '')}</p>
                <div className={s.actionsRow}>
                    <button
                        type="button"
                        className={`${s.actionBtn} ${s.actionBtnDanger}`}
                        disabled={busy}
                        onClick={() => void apply(pendingConfirm)}
                    >
                        {t(pendingConfirm.labelKey)}
                    </button>
                    <button
                        ref={keepRef}
                        type="button"
                        className={s.ghostBtn}
                        disabled={busy}
                        onClick={() => setPendingConfirm(null)}
                    >
                        {t('calendar.peek.action.keepBooking')}
                    </button>
                </div>
            </div>
        );
    }

    if (actions.length === 0) return null;

    return (
        <div className={compact ? s.actionsCompact : s.actions} onClick={e => e.stopPropagation()}>
            {!compact && <span className={s.actionsLabel}>{t('calendar.peek.updateStatus')}</span>}
            <div className={s.actionsRow}>
                {actions.map(view => (
                    <button
                        key={view.target}
                        type="button"
                        className={view.needsConfirm ? s.actionBtnSecondary : s.actionBtn}
                        disabled={busy}
                        onClick={() => onClick(view)}
                    >
                        {t(view.labelKey)}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default BookingActions;
